/**
 * PostHog analytics wrapper for the frontend.
 * No-op when VITE_POSTHOG_KEY is not set.
 *
 * Event naming: snake_case, noun_verb_past_tense (e.g. plan_created).
 * To add a new event, add it to EventMap below — TS will catch typos and missing props.
 */
import posthog from 'posthog-js'

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
// First-party reverse-proxy path (see vercel.json + vite.config.ts). Bypasses content blockers.
const POSTHOG_HOST = '/ingest'
const POSTHOG_UI_HOST = 'https://eu.posthog.com'

let initialised = false

export function initAnalytics(): void {
  if (!POSTHOG_KEY || initialised) return
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: POSTHOG_UI_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: true,
    persistence: 'localStorage',
  })
  initialised = true
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialised) return
  posthog.captureException(err, context)
}

export function identifyUser(userId: string, email?: string): void {
  if (!initialised) return
  posthog.identify(userId, email ? { email } : undefined)
}

export function resetAnalytics(): void {
  if (!initialised) return
  posthog.reset()
}

export type EventMap = {
  // Auth
  login: void
  signup: void
  signout: void
  password_reset_requested: void
  password_reset_completed: void

  // Onboarding
  onboarding_step_completed: { step: 1 | 2 | 3 | 4 }
  onboarding_completed: {
    is_tour_operator: boolean
    uses_external_partners: boolean
    has_connections: boolean
  }

  // Plans
  plan_created: { plan_id: string; direction?: string; airport_code?: string }
  plan_deleted: { plan_id: string }
  plan_stage_changed: { plan_id: string; to_stage: 'draft' | 'active' | 'archived' }

  // Arrivals
  arrivals_added: { plan_id: string; count: number; source: 'single_form' | 'bulk_paste' }
  arrivals_edited: { plan_id: string; field_changed: string }
  arrivals_deleted: { plan_id: string; count: number }

  // Optimization
  optimization_started: { plan_id: string }
  optimization_cancelled: { plan_id: string }
  flight_lookup_performed: { plan_id: string; result_count: number }

  // Solution / versions
  solution_exported: { plan_id: string; format: string }
  version_publish_clicked: { plan_id: string; version_id?: string }

  // Destinations
  destination_created: void
  destination_updated: void
  destination_deleted: void

  // Coach partners (CRUD only — block/allocation events fire from backend)
  coach_partner_created: void
  coach_partner_updated: void
  coach_partner_deleted: void

  // Account
  profile_updated: { field_updated: string }

  // Airport config + regions
  airport_config_updated: {
    change_type:
      | 'config_created'
      | 'config_updated'
      | 'config_deleted'
      | 'rule_added'
      | 'rule_deleted'
      | 'transport_mode_added'
      | 'transport_mode_updated'
      | 'transport_mode_deleted'
  }
  region_created: void
  region_updated: void
  region_deleted: void

  // Public routes (no auth, anonymous distinct_id)
  partner_link_viewed: { link_token_prefix: string; partner_name?: string }
  partner_link_access_denied: { reason: 'expired' | 'revoked' | 'not_found' }
  driver_sheet_viewed: { token_prefix: string; bus_number?: number }
  day_plan_sheet_viewed: { token_prefix: string }
}

export type EventName = keyof EventMap

export function trackEvent<E extends EventName>(
  event: E,
  ...props: EventMap[E] extends void ? [] : [EventMap[E]]
): void {
  if (!initialised) return
  posthog.capture(event, props[0] as Record<string, unknown> | undefined)
}
